/**
 * /notifications — the in-app inbox.
 *
 * Server-paginated (G13) via the apps/notifications inbox endpoint.
 * URL-driven state: `status` filter + `page`. Clicking a row marks it
 * read and routes to the detail page.
 *
 * No permission gate — every authenticated user has an inbox. The
 * server scopes reads by `userId` from the session, so a user only
 * sees their own notifications regardless of role.
 */

import type { Notification } from '@clockwise/clients/notifications';
import { NOTIFICATION_STATUSES } from '@clockwise/clients/notifications';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { notificationsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const search = z.object({
  status: z.enum(NOTIFICATION_STATUSES).optional(),
  unreadOnly: z.coerce.boolean().optional(),
  page: z.coerce.number().int().min(0).optional(),
});

export const Route = createFileRoute('/_authenticated/notifications/')({
  validateSearch: search,
  component: Inbox,
});

const PAGE_SIZE = 20;

function Inbox() {
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const [_pendingId, setPendingId] = useState<string | null>(null);

  const page = s.page ?? 0;
  const query = {
    ...(s.status ? { status: s.status } : {}),
    ...(s.unreadOnly ? { unreadOnly: true } : {}),
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  } as const;

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.notifications.list(query),
    queryFn: () => notificationsApi.list(query),
    placeholderData: keepPreviousData,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onMutate: (id) => setPendingId(id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });

  const items = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const hasNext = (page + 1) * PAGE_SIZE < total;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-6">
      <header className="flex items-center justify-between">
        <h1 className="font-semibold text-2xl">Notifications</h1>
        <div className="flex items-center gap-2">
          <Button
            variant={s.unreadOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  unreadOnly: prev.unreadOnly ? undefined : true,
                  page: 0,
                }),
              })
            }
          >
            Unread only
          </Button>
          <Link to="/setup/notifications">
            <Button variant="ghost" size="sm">
              Preferences
            </Button>
          </Link>
        </div>
      </header>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState filtered={Boolean(s.status || s.unreadOnly)} />
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((n) => (
            <li key={n.id}>
              <NotificationRow
                notification={n}
                onClick={() => {
                  if (!n.readAt) markRead.mutate(n.id);
                  navigate({ to: '/notifications/$id', params: { id: n.id } });
                }}
              />
            </li>
          ))}
        </ul>
      )}

      {items.length > 0 ? (
        <footer className="flex items-center justify-between text-muted-foreground text-sm">
          <span>
            Page {page + 1} · {total} total
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() =>
                navigate({
                  search: (prev) => ({ ...prev, page: Math.max(0, (prev.page ?? 0) - 1) }),
                })
              }
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNext}
              onClick={() =>
                navigate({
                  search: (prev) => ({ ...prev, page: (prev.page ?? 0) + 1 }),
                })
              }
            >
              Next
            </Button>
          </div>
        </footer>
      ) : null}
    </div>
  );
}

function NotificationRow({
  notification,
  onClick,
}: {
  notification: Notification;
  onClick: () => void;
}) {
  const unread = !notification.readAt;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border px-4 py-3 text-left transition-colors hover:bg-accent ${
        unread ? 'border-primary/40 bg-card' : 'border-border bg-muted/30'
      }`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className={`truncate ${unread ? 'font-semibold' : 'font-medium'}`}>
          {notification.title}
        </span>
        <span className="shrink-0 text-muted-foreground text-xs">
          {formatRelative(notification.createdAt)}
        </span>
      </div>
      <p className="mt-1 line-clamp-2 text-muted-foreground text-sm">{notification.body}</p>
    </button>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className="rounded-lg border border-dashed py-12 text-center text-muted-foreground">
      {filtered ? 'No notifications match the current filter.' : "You're all caught up."}
    </div>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const m = Math.floor(diffMs / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}
